<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-product-box" :class="{ loading: isLoading }">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">

      <!-- Toggle buttons -->
      <div class="button-group mb-4">
        <button
          type="button"
          class="btn"
          :class="importType === 'sku' ? 'btn-primary' : 'btn-outline-primary'"
          @click="selectImportType('sku')"
        >
          SKU Data
        </button>
        <button
          type="button"
          class="btn"
          :class="importType === 'order' ? 'btn-primary' : 'btn-outline-primary'"
          @click="selectImportType('order')"
        >
          Order Data
        </button>
      </div>

      <form @submit.prevent="submitForm">
        <div class="row">
          <!-- Table name -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">Data Stream Table Name</label>
              <input
                type="text"
                class="form-control shadow-none rounded-0 text-black"
                v-model="form.dataStreamTableName"
                readonly
              />
            </div>
          </div>

          <!-- Column order -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">Column Order</label>
              <input
                type="text"
                class="form-control shadow-none rounded-0 text-black"
                v-model="form.columnOrder"
                readonly
              />
            </div>
          </div>

          <!-- CSV URI (result of upload) -->
          <div class="col-md-12">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">CSV URI</label>
              <input
                type="text"
                class="form-control shadow-none rounded-0 text-black"
                v-model="form.csvUri"
                placeholder="Will be set automatically after upload"
                readonly
              />
            </div>
          </div>

          <!-- CSV picker -->
          <div class="col-md-12">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">Upload CSV</label>

              <div class="d-flex align-items-center gap-2">
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  @click="openCsvDialog"
                  :disabled="upload.inProgress || isUploading"
                >
                  Choose CSV
                </button>
                <span class="text-muted small" v-if="selectedFileName">{{ selectedFileName }}</span>
                <span class="text-muted small" v-else>No file selected</span>
              </div>

              <div class="csv-dropzone mt-2" @dragover.prevent @drop.prevent="onCsvDrop">
                Drag & drop CSV here
              </div>

              <input
                ref="csvInput"
                type="file"
                accept=".csv,text/csv"
                class="d-none"
                @change="onCsvChange"
              />
            </div>
          </div>

          <!-- Upload progress -->
          <div class="col-md-12" v-if="upload.inProgress">
            <div class="mb-2 small text-muted">
              <span v-if="upload.phase === 'uploading'">Uploading…</span>
              <span v-else-if="upload.phase === 'committing'">Finalising…</span>
              <span v-else-if="upload.phase === 'done'">Completed</span>
              {{ humanBytes(upload.loaded) }} / {{ humanBytes(upload.total) }}
              <span v-if="upload.percent >= 0"> ({{ upload.percent }}%)</span>
            </div>
            <div class="progress mb-3" style="height: 8px;">
              <div
                class="progress-bar"
                role="progressbar"
                :style="{ width: upload.percent + '%'}"
                :aria-valuenow="upload.percent"
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
          </div>

          <!-- Field mapper -->
          <div class="col-md-12">
            <FieldMapper
              v-if="mapperReady && !mapperCrashed && Array.isArray(sourceFields) && Array.isArray(targetFields)"
              :sourceFields="sourceFields"
              :targetFields="targetFields"
              @columnOrderGenerated="setColumnOrder"
            />
          </div>

          <div class="col-12 mt-3">
            <button class="btn btn-primary" type="submit" :disabled="isLoading || upload.inProgress || !form.csvUri">
              Submit Import
            </button>
          </div>
        </div>
      </form>
    </div>

    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, nextTick, onErrorCaptured } from "vue";
import axios from "axios";
import moment from "moment";
import FieldMapper from "@/components/ELSA/DataImport/FieldMapper.vue";
import { useRouter } from "vue-router";
import { BASE_HSUITE_URL } from "@/core/constant/env";
import { BlobServiceClient, StorageRetryPolicyType } from "@azure/storage-blob";

type UploadPhase = "idle" | "uploading" | "committing" | "done" | "error";

export default defineComponent({
  name: "ImportCsvForm",
  components: { FieldMapper },
  props: {
    dataStreamID: { type: Number, required: true },
    leadID: { type: Number, required: true },
  },
  setup(props) {
    // --- state ---
    const form = ref({
      dataStreamTableName: "Order Data - Standard",
      columnOrder: "",
      csvUri: "",
      mySQLtableName: "",
      customerName: "",
      enquiryID: null as number | null,
    });

    const isLoading = ref(false);
    const isUploading = ref(false);

    const upload = ref({
      inProgress: false,
      loaded: 0,
      total: 0,
      percent: 0,
      phase: "idle" as UploadPhase,
    });

    // CSV picker
    const csvInput = ref<HTMLInputElement | null>(null);
    const selectedFileName = ref<string>("");

    // Mapper visibility guards
    const mapperReady = ref(false);
    const mapperCrashed = ref(false);

    const sourceFields = ref<string[]>([]);
    const targetFieldsSKU = [
      "IDPrimary","SKUID","ItemDescription","UnitQuantity","UnitsInStock",
      "InnersInStock","InnersInOuter","OutersInPallet","UnitsSold_PeakMonth",
      "UnitsSold_Annually","UnitWidth","UnitDepth","UnitHeight","UnitWeight",
      "InnerWidth","InnerDepth","InnerHeight","InnerWeight","OuterWidth",
      "OuterDepth","OuterHeight","OuterWeight","ProductFamily","Miscellaneous1",
      "Miscellaneous2","Miscellaneous3","Miscellaneous4","Miscellaneous5","UnitDensity",
    ];
    const targetFieldsOrder = [
      "RunID","OrderID","SKUID","UnitQuantity","OrderDate",
      "DispatchDate","OrderType","Miscellaneous1","Miscellaneous2",
      "Miscellaneous3","Miscellaneous4","Miscellaneous5",
    ];
    const targetFields = ref<string[]>(targetFieldsOrder);
    const importType = ref<"sku" | "order">("order");

    const router = useRouter();

    onMounted(async () => {
      await nextTick();
      mapperReady.value = true;
    });
    onErrorCaptured((err) => {
      console.error("Child render error:", err);
      mapperCrashed.value = true;
      return false;
    });

    // --- helpers ---
    const humanBytes = (n: number) => {
      if (n === undefined || n === null) return "";
      const units = ["B", "KB", "MB", "GB"];
      let i = 0; let v = n;
      while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
      return `${v.toFixed(1)} ${units[i]}`;
    };

    const uniqueBlobName = (originalName: string) => {
      const extIdx = originalName.lastIndexOf(".");
      const base = extIdx > -1 ? originalName.slice(0, extIdx) : originalName;
      const ext  = extIdx > -1 ? originalName.slice(extIdx) : "";
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const rand =
        (window.crypto && "randomUUID" in window.crypto)
          ? (window.crypto as any).randomUUID()
          : Math.random().toString(36).slice(2);
      return `${base}__${stamp}__${rand}${ext}`;
    };

    // Parse only the header line to drive FieldMapper
    const parseCsvHeaders = (file: File): Promise<string[]> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Failed to read CSV"));
        reader.onload = () => {
          try {
            const text = String(reader.result ?? "");
            const firstLine = text.split(/\r?\n/).find(l => l.trim().length > 0) ?? "";
            const headers = firstLine.split(",").map(h => h.trim()).filter(Boolean);
            resolve(headers);
          } catch (e) {
            reject(e);
          }
        };
        // first 64KB is plenty for headers
        const blob = file.slice(0, 64 * 1024);
        reader.readAsText(blob);
      });

    // Replace with server-minted short-lived SAS
    const getSasForContainer = async (): Promise<{ account: string; container: string; sas: string }> => {
      return {
        account: "hsuiteblob",
        container: "elsadataimports",
        sas: "sv=2024-11-04&ss=bfqt&srt=sco&sp=rwdlacupiytfx&se=2026-09-21T17:03:14Z&st=2025-09-22T08:48:14Z&spr=https&sig=modcGtHjvH9ny8l0vheGfx%2FXq1t7zHz1ulM9ozZk5Nw%3D",
      };
    };

    // --- CSV picker handlers ---
    const openCsvDialog = () => {
      if (upload.value.inProgress || isUploading.value) return;
      csvInput.value?.click();
    };

    const onCsvChange = async (e: Event) => {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;

      selectedFileName.value = file.name;

      // 1) parse headers so FieldMapper shows immediately
      try {
        sourceFields.value = await parseCsvHeaders(file);
      } catch (err) {
        console.error(err);
        alert("Could not read CSV header.");
        return;
      }

      // 2) start upload
      await handleFileSelected(file);

      input.value = ""; // allow re-selecting the same file later
    };

    const onCsvDrop = async (e: DragEvent) => {
      if (upload.value.inProgress || isUploading.value) return;
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;

      if (!/\.csv$/i.test(file.name) && file.type !== "text/csv") {
        alert("Please drop a .csv file.");
        return;
      }

      selectedFileName.value = file.name;

      // 1) parse headers first
      try {
        sourceFields.value = await parseCsvHeaders(file);
      } catch (err) {
        console.error(err);
        alert("Could not read CSV header.");
        return;
      }

      // 2) upload
      await handleFileSelected(file);
    };

    // --- Azure upload ---
    const handleFileSelected = async (file: File) => {
      if (isUploading.value) {
        console.warn("An upload is already in progress.");
        return;
      }

      isUploading.value = true;
      upload.value.inProgress = true;
      upload.value.phase = "uploading";
      upload.value.loaded = 0;
      upload.value.total = file.size;
      upload.value.percent = 0;

      try {
        const { account, container, sas } = await getSasForContainer();

        const svc = new BlobServiceClient(
          `https://${account}.blob.core.windows.net?${sas}`,
          undefined,
          {
            retryOptions: {
              maxTries: 6,
              tryTimeoutInMs: 120_000,
              retryPolicyType: StorageRetryPolicyType.EXPONENTIAL,
            },
          }
        );

        const containerClient = svc.getContainerClient(container);
        const blobName = uniqueBlobName(file.name);
        const blob = containerClient.getBlockBlobClient(blobName);

        await blob.uploadBrowserData(file, {
          blobHTTPHeaders: { blobContentType: file.type || "application/octet-stream" },
          maxSingleShotSize: 0,      // always chunk
          blockSize: 2 * 1024 * 1024,
          concurrency: 2,
          onProgress: (ev) => {
            upload.value.loaded = ev.loadedBytes ?? 0;
            upload.value.percent = upload.value.total
              ? Math.min(100, Math.round((upload.value.loaded / upload.value.total) * 100))
              : 0;
            if (upload.value.percent === 100 && upload.value.phase === "uploading") {
              upload.value.phase = "committing";
            }
          },
        });

        // No extra HEAD request (avoids a CORS preflight)
        form.value.csvUri = blob.url;
        upload.value.phase = "done";
      } catch (err: any) {
        console.error("Upload failed", err);
        upload.value.phase = "error";
        alert(err?.message ?? "Azure upload failed.");
      } finally {
        upload.value.inProgress = false;
        isUploading.value = false;
      }
    };

    // --- backend helpers / submit ---
    const fetchProjectDetails = async () => {
      const resp = await axios.get(`${BASE_HSUITE_URL}/api/Project/GetProjectByLeadID/${props.leadID}`);
      const projectDetails = resp.data.projectDetails;
      form.value.customerName = projectDetails.clientName;
      form.value.enquiryID = projectDetails.enquiryID;
      generateMySQLTableName();
    };

    const generateMySQLTableName = () => {
      const timestamp = moment().format("YYYYMMDDHHmmssSS");
      const customerNameShort = (form.value.customerName || "")
        .replace(/\s+/g, "")
        .substring(0, 8)
        .toUpperCase();
      const prefix = importType.value === "sku" ? "SKUData_" : "ORDERData_";
      form.value.mySQLtableName = `${prefix}${customerNameShort}_${form.value.enquiryID}_WIP_${timestamp}`;
    };

    const setColumnOrder = (columnOrder: string) => {
      form.value.columnOrder = columnOrder;
    };

    const selectImportType = (type: "sku" | "order") => {
      importType.value = type;
      targetFields.value = type === "sku" ? targetFieldsSKU : targetFieldsOrder;
      form.value.dataStreamTableName = type === "sku" ? "SKU Data - Standard" : "Order Data - Standard";
      generateMySQLTableName();
    };

    const submitForm = async () => {
      if (!form.value.csvUri) {
        alert("Please upload a CSV first.");
        return;
      }
      isLoading.value = true;
      try {
        await fetchProjectDetails();
        const response = await axios.post(`${BASE_HSUITE_URL}/api/DataImport/ImportCsv`, {
          dataStreamID: props.dataStreamID,
          dataStreamTableName: form.value.dataStreamTableName,
          tableName: form.value.mySQLtableName,
          columnOrder: form.value.columnOrder,
          csvUri: form.value.csvUri,
        });
        alert(response.data.message || "Import started.");
        router.push({
          name: "DataTableOverview",
          params: { leadID: props.leadID, dataStreamID: props.dataStreamID },
        });
      } catch (error: any) {
        console.error("Error importing CSV:", error);
        alert(error?.message ?? "Import failed.");
      } finally {
        isLoading.value = false;
      }
    };

    return {
      form,
      isLoading,
      isUploading,
      upload,
      csvInput,
      selectedFileName,
      sourceFields,
      targetFields,
      importType,
      mapperReady,
      mapperCrashed,
      humanBytes,
      openCsvDialog,
      onCsvChange,
      onCsvDrop,
      handleFileSelected,
      setColumnOrder,
      selectImportType,
      submitForm,
    };
  },
});
</script>

<style scoped>
.button-group {
  display: flex;
  justify-content: center;
  gap: 10px;
}
.button-group .btn { flex: none; width: 120px; }
.button-group .btn-outline-primary {
  background-color: #fff;
  border: 1px solid #007bff;
  color: #007bff;
}
.button-group .btn-primary {
  background-color: #007bff;
  color: #fff;
  border: 1px solid #007bff;
}

.csv-dropzone {
  border: 1px dashed #adb5bd;
  border-radius: 6px;
  padding: 12px;
  text-align: center;
  color: #6c757d;
}
.csv-dropzone:hover {
  border-color: #007bff;
  color: #007bff;
}

.progress { background: #e9ecef; }
.progress-bar { background: #007bff; }

.loading-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(255,255,255,0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.loading { cursor: wait; }
</style>
